import { useParams, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ArrowLeft,
  Loader2,
  Pause,
  Play,
  Download,
  Eye,
  MousePointer,
  Target,
  FileText,
  Package,
  Store,
  Lock,
  Activity,
} from 'lucide-react';
import { useTier } from '@/hooks/useTier';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent, EmptyState, ErrorState, useToast, useConfirm } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mustauto/ui/web';
import {
  useCampaignDetail,
  usePauseCampaign,
  useResumeCampaign,
} from '@/hooks/useCampaignDetail';
import {
  useCampaignStorefront,
  useUpsertStorefront,
} from '@/hooks/useCampaignCatalog';
import { formatReais, formatNumber, formatPercent } from '@/lib/utils';
import { api } from '@/lib/api';
import { CampaignProductsTab } from '@/components/CampaignProductsTab';
import { Breadcrumbs } from '@mustauto/ui/web';

const SURFACE_LABELS: Record<string, string> = {
  mechanic_os: 'OS do mecânico',
  b2c_quote: 'Orçamento do cliente',
  b2c_app: 'App B2C',
};

export function CampaignDetailPage() {
  const { campaignId } = useParams({ from: '/app/campaigns/$campaignId' });
  const { data, isLoading, isError, error, refetch } = useCampaignDetail(campaignId);
  const pauseMutation = usePauseCampaign();
  const resumeMutation = useResumeCampaign();
  const { data: storefront } = useCampaignStorefront(campaignId);
  const upsertStorefrontMutation = useUpsertStorefront(campaignId);
  const { canAccess } = useTier();
  const canExportPdf = canAccess('pdfExport');
  const { toast } = useToast();
  const confirm = useConfirm();

  const [sfStoreName, setSfStoreName] = useState('');
  const [sfSearchUrl, setSfSearchUrl] = useState('');
  const [activeTab, setActiveTab] = useState<'overview' | 'products'>('overview');

  // Erro antes do carregando — ver a nota do Dashboard. Esta e a tela do
  // consumo de budget: o parceiro vinha aqui saber quanto ja gastou.
  if (isError && !data) {
    return (
      <ErrorState
        title="Não foi possível carregar a campanha"
        error={error}
        onRetry={() => void refetch()}
      />
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { campaign, budget, impressionsByDay, bySurface, topVehicles } = data;
  const totalSurface = bySurface.mechanic_os + bySurface.b2c_quote + bySurface.b2c_app;

  function surfacePercent(value: number): number {
    return totalSurface > 0 ? Math.round((value / totalSurface) * 100) : 0;
  }

  async function handleExportPdf() {
    try {
      const res = await api.get(
        `/partners/my/campaigns/${campaignId}/export?format=pdf`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `campanha-${campaign.name.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({
        type: 'error',
        title: 'Não foi possível gerar o PDF. Tente novamente.',
      });
    }
  }

  async function handlePause() {
    const ok = await confirm({
      title: 'Pausar a campanha?',
      description: 'Ela para de ser exibida nas oficinas.',
      confirmLabel: 'Pausar',
    });
    if (!ok) return;
    pauseMutation.mutate(campaignId, {
      onSuccess: () => toast({ type: 'success', title: 'Campanha pausada' }),
      onError: () =>
        toast({
          type: 'error',
          title: 'Não foi possível pausar a campanha. Tente novamente.',
        }),
    });
  }

  function handleResume() {
    resumeMutation.mutate(campaignId, {
      onSuccess: () => toast({ type: 'success', title: 'Campanha retomada' }),
      onError: () =>
        toast({
          type: 'error',
          title: 'Não foi possível retomar a campanha. Tente novamente.',
        }),
    });
  }

  const isToggling = pauseMutation.isPending || resumeMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Breadcrumbs
          items={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Campanhas', href: '/campaigns' },
            { label: campaign.name },
          ]}
        />
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{campaign.name}</h1>
            <Badge variant="outline">{campaign.category}</Badge>
            <Badge variant={campaign.active ? 'success' : 'secondary'}>
              {campaign.active ? 'Ativa' : 'Pausada'}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {campaign.active ? (
              <button
                onClick={handlePause}
                disabled={isToggling}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted disabled:opacity-50"
              >
                <Pause size={14} />
                Pausar
              </button>
            ) : (
              <button
                onClick={handleResume}
                disabled={isToggling}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                <Play size={14} />
                Retomar
              </button>
            )}
            <button
              onClick={handleExportPdf}
              disabled={!canExportPdf}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              title={canExportPdf ? 'Exportar PDF' : 'Disponível a partir do plano Ouro'}
            >
              {canExportPdf ? <Download size={14} /> : <Lock size={14} />}
              Exportar PDF
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      {campaign.displayMode === 'catalog' && (
        <div className="flex gap-1 border-b border-border">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overview' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Visao geral
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'products' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Produtos
          </button>
        </div>
      )}

      {/* Products tab */}
      {activeTab === 'products' && campaign.displayMode === 'catalog' && (
        <CampaignProductsTab campaignId={campaignId} />
      )}

      {/* Overview tab content */}
      {activeTab === 'overview' && <>
      {/* Budget bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between text-sm">
            <span>
              <span className="font-bold">{formatReais(budget.used)}</span>{' '}
              de {formatReais(budget.total)} usados
            </span>
            <span className="text-muted-foreground">
              {budget.daysRemaining} dias restantes
            </span>
          </div>
          <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{
                width: `${Math.min((budget.used / (budget.total || 1)) * 100, 100)}%`,
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Catalog / Storefront management */}
      {campaign.displayMode === 'catalog' && (
        <Card>
          <CardContent className="flex items-center justify-between pt-6">
            <div className="flex items-center gap-3">
              <Package size={20} className="text-primary" />
              <div>
                <div className="text-sm font-bold">Catálogo de produtos</div>
                <div className="text-xs text-muted-foreground">
                  Gerencie os produtos que aparecem na tela "Onde Comprar"
                </div>
              </div>
            </div>
            <Link
              to="/campaigns/$campaignId/catalog"
              params={{ campaignId }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              <Package size={14} />
              Gerenciar catálogo
            </Link>
          </CardContent>
        </Card>
      )}

      {campaign.displayMode === 'storefront' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Store size={18} />
              Configuração da vitrine
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {storefront ? (
              <div className="rounded-lg bg-muted p-3 space-y-1">
                <div className="text-sm font-medium">{storefront.storeName}</div>
                <div className="text-xs text-muted-foreground break-all">
                  {storefront.searchUrlTemplate}
                </div>
                {storefront.avgRating && (
                  <div className="text-xs text-muted-foreground">
                    Avaliação: {storefront.avgRating}/5
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  value={sfStoreName}
                  onChange={(e) => setSfStoreName(e.target.value)}
                  placeholder="Nome da loja"
                  className="w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm outline-none focus:border-primary"
                />
                <input
                  type="text"
                  value={sfSearchUrl}
                  onChange={(e) => setSfSearchUrl(e.target.value)}
                  placeholder="URL de busca com {query}"
                  className="w-full rounded-lg border border-border bg-muted px-3 py-2 text-sm outline-none focus:border-primary"
                />
                <button
                  onClick={() => {
                    if (sfStoreName && sfSearchUrl.includes('{query}')) {
                      upsertStorefrontMutation.mutate({
                        storeName: sfStoreName,
                        searchUrlTemplate: sfSearchUrl,
                      });
                    }
                  }}
                  disabled={!sfStoreName || !sfSearchUrl.includes('{query}') || upsertStorefrontMutation.isPending}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
                >
                  {upsertStorefrontMutation.isPending ? 'Salvando...' : 'Salvar vitrine'}
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Segmentation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Segmentação</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Regiões</div>
              <div className="mt-1 text-sm font-medium">
                {campaign.regions && campaign.regions.length > 0
                  ? campaign.regions.join(', ')
                  : 'Todas'}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Superfícies</div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {campaign.surfaces && campaign.surfaces.length > 0
                  ? campaign.surfaces.map((s) => (
                      <Badge key={s} variant="outline">
                        {SURFACE_LABELS[s] ?? s}
                      </Badge>
                    ))
                  : <span className="text-sm font-medium">Todas</span>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Impressions chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Impressões diárias (últimos 30 dias)</CardTitle>
        </CardHeader>
        <CardContent>
          {impressionsByDay.length === 0 ? (
            <EmptyState
              compact
              icon={<Activity size={26} />}
              title="Nenhuma impressão ainda"
              description="O gráfico começa a preencher assim que a campanha for exibida numa OS."
            />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={impressionsByDay}>
                <defs>
                  <linearGradient id="fillImpressions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v: string) =>
                    new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                  }
                  className="text-xs"
                />
                <YAxis className="text-xs" />
                <Tooltip
                  labelFormatter={(v) =>
                    new Date(String(v)).toLocaleDateString('pt-BR')
                  }
                  formatter={(value) => [formatNumber(Number(value)), 'Impressões']}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="var(--color-primary)"
                  fill="url(#fillImpressions)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Eye size={16} />
              <span className="text-xs">Impressões</span>
            </div>
            <div className="mt-1 text-2xl font-bold">{formatNumber(data.totalImpressions)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground">
              <MousePointer size={16} />
              <span className="text-xs">Cliques</span>
            </div>
            <div className="mt-1 text-2xl font-bold">{formatNumber(data.totalClicks)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Target size={16} />
              <span className="text-xs">CTR</span>
            </div>
            <div className="mt-1 text-2xl font-bold">{formatPercent(data.ctr)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground">
              <FileText size={16} />
              <span className="text-xs">Orçamentos influenciados</span>
            </div>
            <div className="mt-1 text-2xl font-bold">{formatNumber(data.influencedQuotes)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Surface breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Distribuição por superfície</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(
              [
                ['mechanic_os', bySurface.mechanic_os],
                ['b2c_quote', bySurface.b2c_quote],
                ['b2c_app', bySurface.b2c_app],
              ] as const
            ).map(([key, value]) => (
              <div key={key}>
                <div className="flex items-center justify-between text-sm">
                  <span>{SURFACE_LABELS[key]}</span>
                  <span className="font-medium">
                    {formatNumber(value)} ({surfacePercent(value)}%)
                  </span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${surfacePercent(value)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top vehicles */}
      {topVehicles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Principais veículos alcançados</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Marca</TableHead>
                  <TableHead>Modelo</TableHead>
                  <TableHead className="text-right">Impressões</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topVehicles.map((v) => (
                  <TableRow key={`${v.make}-${v.model}`}>
                    <TableCell>{v.make}</TableCell>
                    <TableCell className="font-medium">{v.model}</TableCell>
                    <TableCell className="text-right">
                      {formatNumber(v.impressions)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      </>}
    </div>
  );
}
